/**
 * FileUploadPanel — carga de archivos GPX y video para una sesión
 *
 * @remarks
 * Dos file inputs independientes: GPX y Video.
 * Sube uno a la vez. Muestra estado por archivo.
 * No bloquea si uno ya fue subido.
 *
 * @module survey/components
 */

import { useState } from 'react';
import { getSurveyApiError, surveyApi } from '../services/survey-api.service';

interface Props {
  sessionId: string;
  onUploaded: () => void;
}

type UploadState = 'idle' | 'uploading' | 'done' | 'error';

interface FileState {
  state: UploadState;
  error: string | null;
  filename: string | null;
}

const INITIAL_FILE_STATE: FileState = { state: 'idle', error: null, filename: null };

async function uploadFile(
  sessionId: string,
  fileType: 'video' | 'gpx',
  file: File,
  setState: (s: FileState) => void,
  onUploaded: () => void
): Promise<void> {
  setState({ state: 'uploading', error: null, filename: file.name });
  try {
    await surveyApi.uploadFile(sessionId, fileType, file);
    setState({ state: 'done', error: null, filename: file.name });
    onUploaded();
  } catch (err) {
    setState({ state: 'error', error: getSurveyApiError(err), filename: file.name });
  }
}

function FileInput({
  label,
  accept,
  fileState,
  onFileChange,
}: {
  label: string;
  accept: string;
  fileState: FileState;
  onFileChange: (file: File) => void;
}): JSX.Element {
  return (
    <div className="sw-file-row">
      <span className="sw-file-label">{label}</span>

      {fileState.state === 'done' ? (
        <span className="sw-file-done">{fileState.filename} — subido</span>
      ) : (
        <label className="sw-file-pick">
          <input
            type="file"
            accept={accept}
            className="sw-file-input-hidden"
            disabled={fileState.state === 'uploading'}
            onChange={e => {
              const file = e.target.files?.[0];
              if (file) onFileChange(file);
            }}
          />
          <span className="sw-file-btn">
            {fileState.state === 'uploading' ? 'Subiendo...' : 'Elegir archivo'}
          </span>
          {fileState.filename && fileState.state !== 'uploading' && (
            <span className="sw-file-name">{fileState.filename}</span>
          )}
        </label>
      )}

      {fileState.state === 'error' && <span className="sw-file-error">{fileState.error}</span>}
    </div>
  );
}

export function FileUploadPanel({ sessionId, onUploaded }: Props): JSX.Element {
  const [gpxState, setGpxState] = useState<FileState>(INITIAL_FILE_STATE);
  const [videoState, setVideoState] = useState<FileState>(INITIAL_FILE_STATE);

  function handleGpx(file: File): void {
    void uploadFile(sessionId, 'gpx', file, setGpxState, onUploaded);
  }

  function handleVideo(file: File): void {
    void uploadFile(sessionId, 'video', file, setVideoState, onUploaded);
  }

  return (
    <div className="sw-upload-panel">
      <h4 className="sw-upload-title">Archivos de la sesión</h4>
      <p className="sw-upload-hint">GPX y video son necesarios para procesar la sesión.</p>

      <FileInput
        label="Recorrido GPX"
        accept=".gpx"
        fileState={gpxState}
        onFileChange={handleGpx}
      />

      <FileInput
        label="Video"
        accept="video/*,.mp4,.mov,.avi"
        fileState={videoState}
        onFileChange={handleVideo}
      />
    </div>
  );
}
